import { makeAutoObservable } from 'mobx'
import { createContext, useContext } from 'react'
import PromotionServiceProvider from '../../services/PromotionServiceProvider'
import { timeout } from '../../theme/timeout'
import {
  EntriesUrl,
  KeywordsAdsUrl,
  PromotionCoverageResponse,
  PromotionResponse,
} from '../../api/promotion/interface'
import { StoreInstances } from '../StoreInstancesContainer'
import { Config } from '../../config/ConfigManager'

import { show as showNiceModal } from '@ebay/nice-modal-react'
import { BannerPromotion } from '../../features/promotion/BannerPromotion'
import { UserAttributeKey } from '../user/interfaces'

interface EntryData {
  totalEntriesWinned: number
  remaningEntryValue: string
}

export class BannerPromotionStore {
  public promotionData: PromotionResponse[] = []

  public entriesData: EntriesUrl | null = null

  public promotionCoverageData: PromotionCoverageResponse | null = null

  public promoOrderNumber: number | null = null

  public matchCoverage = false

  public bannerKeywords: KeywordsAdsUrl | null = null

  public showBottomBannerPromotion = false

  public expandPromoDrawer = false

  public timeOutPromoDrawer: number | null = null

  public entryData: EntryData | null = null

  public expandPromoDrawerCart = false

  public timeOutPromoDrawerCart: number | null = null

  public handleCoverageMatch = (orderNumber: number): void => {
    this.promoOrderNumber = orderNumber
    const coverageData = this.promotionCoverageData
    const coverage: string[] = coverageData?.coverage.split(',') || []
    this.matchCoverage = coverage.includes(String(this.promoOrderNumber))
  }

  public handleBannerPromoDrawerOpenExpanded = (): void => {
    this.expandPromoDrawer = true
    this.timeOutPromoDrawer = window.setTimeout(() => {
      this.handleCloseBannerPromoDrawer()
    }, timeout.closePromoBanner)
  }

  public handleBannerPromoDrawerCartOpenExpanded = (): void => {
    this.expandPromoDrawerCart = true
    this.timeOutPromoDrawerCart = window.setTimeout(() => {
      if (this.timeOutPromoDrawerCart) {
        this.handleCloseBannerPromoCartDrawer()
      }
    }, timeout.closePromoBannerCart)
  }

  public handleCloseBannerPromoDrawer = (): void => {
    if (this.timeOutPromoDrawer) {
      clearInterval(this.timeOutPromoDrawer)
    }
    this.expandPromoDrawer = false
  }

  public handleCloseBannerPromoCartDrawer = (): void => {
    if (this.timeOutPromoDrawerCart) {
      clearInterval(this.timeOutPromoDrawerCart)
    }
    this.expandPromoDrawerCart = false
  }

  constructor() {
    makeAutoObservable(this)
  }

  public saveDateToLocalStorage = (): void => {
    localStorage.setItem('bannerPromoDate', this.getCurrentDate())
  }

  public getCurrentDate = (): string => {
    return new Date().toLocaleDateString('fr-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }

  roundOfTwoDigits = (num: number): string => {
    return num.toString().padStart(2, '0')
  }

  convertMsToHrs = (milliseconds: number): string => {
    let seconds = Math.floor(milliseconds / 1000)
    let minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)

    seconds %= 60
    minutes %= 60

    return `${this.roundOfTwoDigits(hours)}:${this.roundOfTwoDigits(
      minutes
    )}:${this.roundOfTwoDigits(seconds)}`
  }

  fetchCoverageData = async (promoId: number): Promise<void> => {
    try {
      this.promotionCoverageData =
        await PromotionServiceProvider.bannerPromotionCoverageApi(promoId)
    } catch (_e) {
      this.showBottomBannerPromotion = false
    }
  }

  fetchPromotionData = async (): Promise<void> => {
    try {
      this.promotionData = await PromotionServiceProvider.bannerPromotionApi()

      if (this.promotionData[0]?.promoId > 0) {
        this.saveDateToLocalStorage()
        this.fetchCoverageData(this.promotionData[0]?.promoId)
      }

      if (this.promotionData[0]?.status === 'Active') {
        this.setShowBottomBannerPromotion(true)
      }
    } catch (_e) {
      this.showBottomBannerPromotion = false
    }
  }

  fetchEntriesData = async (): Promise<void> => {
    try {
      this.entriesData = await PromotionServiceProvider.getEntries()
    } catch (_e) {
      this.showBottomBannerPromotion = false
    }
  }

  setBannerKeywords(bannerKeywords: KeywordsAdsUrl | null): void {
    this.bannerKeywords = bannerKeywords
  }

  fetchKeywordsAds = async (
    orderNumbers: string,
    aaiaBrands: string
  ): Promise<void> => {
    try {
      const bannerKeywords = await PromotionServiceProvider.bannerKeywordsApi(
        orderNumbers,
        aaiaBrands
      )
      this.setBannerKeywords(bannerKeywords)
    } catch (_e) {
      //
    }
  }

  public setShowBottomBannerPromotion = (value: boolean): void => {
    this.showBottomBannerPromotion = value
  }

  public setEntryData = (data: EntryData): void => {
    this.entryData = data
  }

  public isGeneralAdsEnabled = (): boolean => {
    const ads = StoreInstances?.userStore?.getUserAttribute(
      UserAttributeKey.ads
    )
    return Number(ads) > 0 ? true : false
  }

  public isChannelPartnerAdsEnabled = (): boolean => {
    const ads = StoreInstances?.userStore?.getUserAttribute(
      UserAttributeKey.ads
    )
    return Number(ads) > 0 ? true : false
  }

  public showBannerOnce = async (): Promise<void> => {
    const bannerPromoDate = localStorage.getItem('bannerPromoDate')

    if (bannerPromoDate === null) {
      await this.fetchPromotionData()
      await this.fetchEntriesData()
    } else {
      const date1 = new Date(bannerPromoDate)
      const date2 = new Date(this.getCurrentDate())

      const hrsDiff = this.convertMsToHrs(
        date2.getTime() - date1.getTime()
      )?.substring(0, 2)

      if (parseInt(hrsDiff, 10) >= 23) {
        await this.fetchEntriesData()
        await this.fetchPromotionData()
      }
    }

    if (
      Config.promotionsEnabled &&
      StoreInstances.uiStore.isPromoEnabled() &&
      this.promotionData &&
      this.promotionData.length > 0
    ) {
      showNiceModal(BannerPromotion, {
        promotionData: this.promotionData,
      })
    }
  }
}
export const BannerPromotionContext = createContext<
  BannerPromotionStore | undefined
>(undefined)

export const useBannerPromotionStore = (): BannerPromotionStore => {
  const bannerPromotionStore = useContext(BannerPromotionContext)
  if (!bannerPromotionStore) {
    throw new Error(
      'No BannerPromotionContext.Provider found when calling useBannerPromotionStore.'
    )
  }
  return bannerPromotionStore
}
